import React from 'react';

// Matches the menu's `max-height`.
const MENU_MAX_HEIGHT = 200;
const VIEWPORT_MARGIN = 8;

// A module function rather than part of the hook: React Compiler cannot model
// the self-referencing rAF closure and fails the whole hook over it.
function callEveryFrame(callback: () => void): () => void {
    let frame = requestAnimationFrame(function track() {
        callback();
        frame = requestAnimationFrame(track);
    });
    return () => cancelAnimationFrame(frame);
}

/**
 * Whether the mention menu should open upwards from the caret: the chat input
 * usually sits at the bottom of its pane, where a dropdown runs off the screen.
 * `optionCount` re-measures as the list grows or shrinks with the query.
 */
export function useMenuOpensAbove(anchor: HTMLElement | null, optionCount: number): boolean {
    const [opensAbove, setOpensAbove] = React.useState(false);

    const measure = React.useCallback(() => {
        if (!anchor) {
            return;
        }
        const { top, bottom } = anchor.getBoundingClientRect();
        const spaceBelow = window.innerHeight - bottom;
        // Only flip when there is genuinely more room above, or a cramped
        // viewport just moves the problem.
        setOpensAbove(spaceBelow < MENU_MAX_HEIGHT + VIEWPORT_MARGIN && top > spaceBelow);
    }, [anchor]);

    React.useEffect(() => {
        if (!anchor) {
            return;
        }

        measure();

        // Lexical repositions the caret anchor after the menu mounts and again
        // as the query changes, without replacing the element or telling us. A
        // measurement taken once therefore goes stale and the menu opens the
        // wrong way, so track the anchor for as long as the menu is open.
        const stopTracking = callEveryFrame(measure);

        // The page can also scroll or resize under the caret.
        window.addEventListener('resize', measure);
        window.addEventListener('scroll', measure, true);
        return () => {
            stopTracking();
            window.removeEventListener('resize', measure);
            window.removeEventListener('scroll', measure, true);
        };
    }, [anchor, optionCount, measure]);

    return opensAbove;
}
